// ============================================================
// O VÍNCULO COM A GENESIS (Story 38.4, issue #690)
//
// O contrato entre a API e a tela: os estados da pessoa, o regime, as categorias
// do desligamento, o que a leitura devolve e os corpos dos pedidos.
//
// As regras de «anulado» e «vivo» em JavaScript vivem aqui, sobre `isLiveOn` (o par
// das alocações): o servidor calcula com elas os campos de cada vínculo, e a tela
// não decide nenhum. O par em SQL é `bondLiveOn`, em `apps/api/src/lib/genesis-bonds.ts`.
// ============================================================
import { z } from 'zod'

import { isLiveOn } from './allocation-live.ts'
import { ContractDateSchema, type CONTRACT_TYPES } from './dtos.ts'

/**
 * O estado do vínculo de uma pessoa, calculado na leitura (sem coluna). A ordem é a
 * do épico, e é a do posto que o SQL devolve (`bondStatusRankSql`):
 *
 *   • `candidate` — nunca foi colaboradora (Candidato);
 *   • `allocated` — tem alocação em vigor (Alocado);
 *   • `bench`     — tem vínculo em vigor, sem alocação em vigor (Sem posição);
 *   • `former`    — já foi colaboradora, e não é (Ex-colaborador).
 */
export const BOND_STATUSES = ['candidate', 'allocated', 'bench', 'former'] as const
export type BondStatus = (typeof BOND_STATUSES)[number]

export const BOND_STATUS_LABELS: Readonly<Record<BondStatus, string>> = {
  candidate: 'Candidato',
  allocated: 'Alocado',
  bench: 'Sem posição',
  former: 'Ex-colaborador',
}

/**
 * O regime efetivo do vínculo: o que a pessoa aceita (`CONTRACT_TYPES`) sem «os
 * dois» — o vínculo é um regime só. `@caliber/db` tem a mesma lista.
 */
export const BOND_CONTRACT_TYPES = ['clt', 'pj'] as const satisfies readonly Exclude<
  (typeof CONTRACT_TYPES)[number],
  'both'
>[]
export type BondContractType = (typeof BOND_CONTRACT_TYPES)[number]

/**
 * A categoria do desligamento. Fechada, e não texto livre: o detalhe fica no RH, e
 * um campo livre que toda a Genesis lê puxaria dado sensível (saúde, disciplina).
 * `@caliber/db` tem a mesma lista.
 */
export const BOND_END_REASONS = [
  'resignation',
  'dismissal',
  'contract_end',
  'regime_change',
  'opened_by_mistake',
  'other',
] as const
export type BondEndReason = (typeof BOND_END_REASONS)[number]

export const BOND_END_REASON_LABELS: Readonly<Record<BondEndReason, string>> = {
  resignation: 'Pedido do profissional',
  dismissal: 'Decisão da Genesis',
  contract_end: 'Fim do contrato sem nova posição',
  regime_change: 'Troca de regime',
  opened_by_mistake: 'Aberto por engano',
  other: 'Outro',
}

/** As duas datas de um vínculo, que é o que as regras abaixo leem. */
export interface BondDates {
  startedOn: string
  endedOn: string | null
}

/** Anulado: zero dias (`endedOn = startedOn`). Nunca esteve em vigor, e é inerte. */
export function isBondAnnulled(b: BondDates): boolean {
  return b.endedOn === b.startedOn
}

/**
 * Vivo em `hoje`: não anulado, e sem fim ou com o fim depois de hoje. Compõe
 * `isLiveOn`, e não reescreve a regra do término. «Em vigor» é
 * `isInEffectOn(startedOn, endedOn, hoje)`, que já exclui o anulado.
 */
export function isBondLiveOn(b: BondDates, hoje: string): boolean {
  return !isBondAnnulled(b) && isLiveOn(b.endedOn, hoje)
}

/** Um vínculo como a leitura o devolve, com os campos que o servidor calcula. */
export interface GenesisBondDto extends BondDates {
  id: string
  /** `null` = «não informado» (só nos vínculos da migração 0072). */
  contractType: BondContractType | null
  /** `null` no aberto e nos fechados pela migração. */
  endReason: BondEndReason | null
  annulled: boolean
  live: boolean
  inEffect: boolean
  /** O mais recente: o não anulado de maior início. */
  latest: boolean
  createdAt: string
}

/** `GET /api/professionals/:id/bonds`. */
export interface ProfessionalBondsResponse {
  status: BondStatus
  bonds: GenesisBondDto[]
}

const BondContractTypeSchema = z.enum(BOND_CONTRACT_TYPES)

/** `POST /api/professionals/:id/bonds` — abrir um vínculo. */
export const OpenBondSchema = z.object({
  contractType: BondContractTypeSchema,
  startedOn: ContractDateSchema,
})
export type OpenBondInput = z.infer<typeof OpenBondSchema>

/** `POST /api/professionals/:id/bonds/:bondId/close` — desligar da Genesis. */
export const CloseBondSchema = z.object({
  endedOn: ContractDateSchema,
  endReason: z.enum(BOND_END_REASONS),
})
export type CloseBondInput = z.infer<typeof CloseBondSchema>

/** `PATCH /api/professionals/:id/bonds/:bondId` — corrigir o regime. */
export const CorrectBondSchema = z.object({
  contractType: BondContractTypeSchema,
})
export type CorrectBondInput = z.infer<typeof CorrectBondSchema>
